import { motion } from 'framer-motion';
import { useEffect, useMemo } from 'react';
import type { PublicPlayer, Snapshot } from 'shared';
import { QrPanel } from '../components/QrPanel';
import { BallotRow, hiddenBallots, revealedBallots } from '../components/BallotRow';
import { GovernmentBand, GovernmentBlock, type GovernmentPair } from '../components/Government';
import { ProjectorFrame } from '../components/ProjectorFrame';
import { Roster } from '../components/Roster';
import { Halftone, Label, Paper } from '../design/Paper';
import { Stamp } from '../design/Stamp';
import { Tracker } from '../design/Tracker';
import { tiltFor } from '../design/tokens';
import { ro } from '../i18n/ro';
import { playVoteCue, unlockAudio } from '../state/cues';
import { useRoom } from '../state/RoomConnection';
import { justRevealed, voteJustOpened } from '../state/connection';
import { HistoryScreen } from './HistoryScreen';

/** Resolves a government's player ids against the current roster. */
function pairOf(snapshot: Snapshot, gov: { presidentId: string; chancellorId: string } | null): GovernmentPair {
  const find = (id: string): PublicPlayer | null =>
    snapshot.players.find((p) => p.id === id) ?? null;
  if (!gov) return { president: null, chancellor: null };
  return { president: find(gov.presidentId), chancellor: find(gov.chancellorId) };
}

/**
 * The shared screen. Read-only, and always displaying something meaningful
 * about the room. It is also the primary alert: the one device guaranteed
 * awake, plugged in and attached to a speaker everyone can hear.
 */
export function ProjectorScreen() {
  const room = useRoom();
  const snapshot = room.snapshot;

  // Autoplay policy needs a gesture, but a projector is opened deliberately and
  // typically clicked into fullscreen, so try to unlock on any interaction.
  useEffect(() => {
    const unlock = () => unlockAudio();
    window.addEventListener('pointerdown', unlock, { once: true });
    window.addEventListener('keydown', unlock, { once: true });
    return () => {
      window.removeEventListener('pointerdown', unlock);
      window.removeEventListener('keydown', unlock);
    };
  }, []);

  const opened = voteJustOpened(room);
  useEffect(() => {
    if (opened) playVoteCue();
  }, [opened]);

  // Only a device that *witnessed* the transition plays the set-piece; one that
  // arrives later renders the finished state with no replay.
  const revealing = justRevealed(room);

  // The phase and version on the document element: the two things every e2e
  // assertion waits on, without reaching into a component's markup.
  useEffect(() => {
    const el = document.documentElement;
    el.dataset.phase = snapshot?.phase ?? 'CONNECTING';
    el.dataset.version = String(snapshot?.version ?? 0);
    el.dataset.projection = snapshot?.projection ?? 'LIVE';
  }, [snapshot?.phase, snapshot?.version, snapshot?.projection]);

  if (!snapshot) {
    return <ProjectorMessage title={ro.connecting} />;
  }

  if (snapshot.phase === 'CLOSED') {
    return <ProjectorMessage title={ro.roomClosed} body={ro.roomClosedBody} />;
  }

  if (snapshot.projection === 'HISTORY') {
    return <HistoryScreen snapshot={snapshot} />;
  }

  if (snapshot.inChaos) {
    return <ProjectorChaos />;
  }

  const session = snapshot.session;

  if (session) {
    return (
      <SessionView snapshot={snapshot} animateReveal={revealing} announceVote={opened} />
    );
  }

  // No session. Before the first vote the screen is a join invitation; after
  // one, it holds the sitting government.
  if (snapshot.phase === 'LOBBY') {
    return <ProjectorLobby snapshot={snapshot} />;
  }

  return (
    <ProjectorFrame kicker={ro.noVoting} failedElections={snapshot.failedElections}>
      <div className="flex shrink-0 flex-col items-center">
        <Label className="text-screen-sm text-ink-soft">
          {snapshot.electedGovernment ? ro.electedGovernment : ro.noGovernment}
        </Label>
        {snapshot.electedGovernment && (
          <div className="mt-scr-2">
            <GovernmentBlock
              pair={pairOf(snapshot, snapshot.electedGovernment)}
              termLimitedIds={snapshot.termLimitedPlayerIds}
            />
          </div>
        )}
      </div>
      <div className="min-h-0 w-full max-w-[70cqw] shrink">
        <Roster players={snapshot.players} />
      </div>
    </ProjectorFrame>
  );
}

/**
 * The voting and revealed phases share one tree. The children are keyed, so
 * React keeps the ballot row's card instances across the phase change and the
 * cards turn from face-down rather than mounting already turned.
 */
function SessionView({
  snapshot,
  animateReveal,
  announceVote,
}: {
  snapshot: Snapshot;
  animateReveal: boolean;
  announceVote: boolean;
}) {
  const session = snapshot.session!;
  const revealed = session.status === 'REVEALED';
  const passed = revealed && session.outcome === 'PASSED';
  const pair = pairOf(snapshot, session.government);

  return (
    <ProjectorFrame
      kicker={
        revealed
          ? passed
            ? ro.electedGovernment
            : ro.proposedGovernment
          : ro.votingInProgress
      }
      meta={
        session.status === 'VOTING'
          ? ro.votesCast(session.votesCast, session.eligibleCount)
          : undefined
      }
      failedElections={snapshot.failedElections}
      announceVote={announceVote}
    >
      <div key="lead" className="flex w-full shrink-0 items-center justify-center gap-scr-4">
        {revealed ? (
          <>
            <Verdict passed={passed} land={animateReveal} />
            <Score
              ja={session.jaCount}
              nein={session.neinCount}
              animate={animateReveal}
            />
          </>
        ) : (
          <Label className="text-screen-sm text-ink-soft">{ro.proposedGovernment}</Label>
        )}
      </div>

      <div key="government" className="shrink-0">
        {revealed ? (
          <GovernmentBand pair={pair} />
        ) : (
          <GovernmentBlock pair={pair} termLimitedIds={snapshot.termLimitedPlayerIds} />
        )}
      </div>

      <BallotRow
        key="ballots"
        animate={animateReveal || !revealed}
        ballots={
          session.status === 'VOTING'
            ? hiddenBallots(snapshot.players, session.eligibleVoterIds, session.votedPlayerIds)
            : revealedBallots(snapshot.players, session.votes)
        }
      />
    </ProjectorFrame>
  );
}

/** The stamped verdict, which lands rather than fades. */
function Verdict({ passed, land }: { passed: boolean; land: boolean }) {
  return (
    <motion.div
      className="shrink-0"
      initial={land ? { scale: 2.1, rotate: -9, opacity: 0 } : false}
      animate={{ scale: 1, rotate: 0, opacity: 1 }}
      transition={land ? { type: 'spring', stiffness: 320, damping: 18, delay: 0.75 } : { duration: 0 }}
    >
      <Stamp tone={passed ? 'teal' : 'red'} size="lg" className={tiltFor('verdict')}>
        {passed ? ro.governmentApproved : ro.governmentRejected}
      </Stamp>
    </motion.div>
  );
}

function Score({ ja, nein, animate }: { ja: number; nein: number; animate: boolean }) {
  return (
    <motion.div
      className="flex shrink-0 items-baseline gap-[1.2cqw] font-display leading-none"
      initial={animate ? { opacity: 0, y: 12 } : false}
      animate={{ opacity: 1, y: 0 }}
      transition={animate ? { duration: 0.35, delay: 0.6 } : { duration: 0 }}
    >
      <span className="text-[7cqh] text-teal">{ja}</span>
      <span className="text-screen-md text-ink-faint">—</span>
      <span className="text-[7cqh] text-red">{nein}</span>
    </motion.div>
  );
}

function ProjectorLobby({ snapshot }: { snapshot: Snapshot }) {
  const joinUrl = useMemo(
    () => `${window.location.origin}/r/${window.location.pathname.split('/')[2] ?? ''}`,
    [],
  );

  return (
    <ProjectorFrame kicker={ro.waitingToStart} failedElections={snapshot.failedElections}>
      <div className="flex min-h-0 w-full flex-1 items-center justify-center gap-scr-4">
        <div className="flex shrink-0 flex-col items-center gap-scr-2">
          <Label className="whitespace-nowrap text-center text-screen-sm">{ro.scanToJoin}</Label>
          <QrPanel url={joinUrl} />
        </div>

        {snapshot.players.length > 0 ? (
          <div className="flex min-h-0 min-w-0 flex-1 flex-col justify-center gap-scr-2 self-stretch">
            <Label className="shrink-0 text-screen-sm text-ink-soft">
              {ro.players} · {snapshot.players.length}
            </Label>
            <Roster players={snapshot.players} />
          </div>
        ) : (
          <Label className="text-screen-md text-ink-faint">{ro.noPlayersYet}</Label>
        )}
      </div>
    </ProjectorFrame>
  );
}

/**
 * Chaos takes over the display entirely, and holds until the next voting
 * session starts.
 */
function ProjectorChaos() {
  return (
    <Paper className="projector flex flex-col" data-testid="chaos">
      <div className="relative flex min-h-0 flex-1 flex-col items-center justify-center gap-scr-4 overflow-hidden bg-red px-scr-gut text-center text-paper">
        <Halftone className="text-paper opacity-30" />
        <div className="chevron-band absolute inset-x-0 top-0 h-[2.2cqh] text-paper opacity-40" />
        <div className="chevron-band absolute inset-x-0 bottom-0 h-[2.2cqh] text-paper opacity-40" />
        <motion.h1
          className="relative max-w-[92cqw] font-display text-screen-hero uppercase leading-[0.95]"
          initial={{ scale: 1.35, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: 'spring', stiffness: 200, damping: 14 }}
        >
          {ro.chaosTitle}
        </motion.h1>
        <motion.p
          className="relative max-w-[52cqw] text-balance text-screen-md leading-relaxed opacity-90"
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 0.9, y: 0 }}
          transition={{ duration: 0.4, delay: 0.35 }}
        >
          {ro.chaosBody}
        </motion.p>
      </div>
      <footer className="shrink-0 bg-paper px-scr-gut py-scr-2">
        <Tracker failedElections={3} size="lg" />
      </footer>
    </Paper>
  );
}

function ProjectorMessage({ title, body }: { title: string; body?: string }) {
  return (
    <Paper className="projector flex flex-col items-center justify-center gap-scr-2 text-center">
      <h1 className="font-display text-screen-lg uppercase">{title}</h1>
      {body && <p className="max-w-[50cqw] text-screen-md text-ink-soft">{body}</p>}
    </Paper>
  );
}
